import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { primaryM, surfaceM, primaryDisabledM, primaryHighlightM, primaryPressedM } from 'web-ui-kit/colors';
import { buttonClicked } from 'web-ui-kit/buttons/events';
import { ButtonLabelStyles, ButtonLabelSmallStyles } from 'web-ui-kit/typography';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject;
/**
 * Use primary button to provide users a primary action.
 */


function PrimaryButton(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$fluid = _ref.fluid,
      fluid = _ref$fluid === void 0 ? false : _ref$fluid,
      _ref$Icon = _ref.Icon,
      Icon = _ref$Icon === void 0 ? null : _ref$Icon,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? 'primary' : _ref$id,
      _ref$labelText = _ref.labelText,
      labelText = _ref$labelText === void 0 ? '' : _ref$labelText,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      _ref$sizeQuery = _ref.sizeQuery,
      sizeQuery = _ref$sizeQuery === void 0 ? '(min-width: 600px)' : _ref$sizeQuery;

  var handleClick = function handleClick(jsEvent) {
    dispatch(buttonClicked({
      disabled: disabled,
      id: id,
      jsEvent: jsEvent,
      labelText: labelText
    }));
  };

  var Button = styles.Button;
  return (
    /*#__PURE__*/
    React.createElement(Button, {
      className: className,
      "data-testid": "".concat(id, "-button"),
      fluid: fluid,
      isDisabled: disabled,
      onClick: handleClick,
      size: size,
      sizeQuery: sizeQuery
    }, labelText, Icon)
  );
}

PrimaryButton.propTypes = {
  /** Allows extending styles with styled(PrimaryButton) constructor */
  className: PropTypes.string,

  /** Whether or not the button is currently disabled */
  disabled: PropTypes.bool,

  /** For reporting events up to parent */
  dispatch: PropTypes.func,

  /** Whether or not the button should have fluid width */
  fluid: PropTypes.bool,

  /**
   * An optional icon placed to the right of the button text.
   * The icon should be sized to fit well in the button, e.g., 24px by 24px.
   */
  Icon: PropTypes.element,

  /**
   * An optional button id string that can be used
   * to help identify the origin of button click events.
   * This string is not user visible.
   */
  id: PropTypes.string,

  /** The button label text */
  labelText: PropTypes.string,

  /**
   * Button size options.
   * If responsive is chosen then button starts at 'small'
   * size and switches to 'medium' size at 'sizeQuery'.
   * One of:
   */
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'responsive']),

  /**
   * If size is 'responsive', mobile first query where button
   * switches from 'small' to 'medium' size.
   */
  sizeQuery: PropTypes.string
};
/**
 * Styles.
 */

var styles = {
  Button: styled.button(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    ", ";\n    ", ";\n\n    display: flex;\n    align-items: center;\n    justify-content: center;\n\n    background-color: ", ";\n    border: none;\n    border-radius: 4px;\n    color: ", ";\n    outline: none;\n    overflow: hidden;\n    padding: 0.5rem 1rem;\n    transition: all 300ms;\n    white-space: nowrap;\n\n    svg {\n      margin-left: 0.5rem;\n    }\n\n    ", "\n\n    ", "\n\n    ", "\n\n    ", "\n  "])), ButtonLabelStyles, function (p) {
    return p.size === 'xsmall' && ButtonLabelSmallStyles;
  }, primaryM, surfaceM, function (p) {
    return p.fluid && "\n      width: 100%;\n    ";
  }, function (p) {
    return p.size === 'medium' && "\n      padding: 0.75rem 1rem;\n    ";
  }, function (p) {
    return p.size === 'responsive' && "\n      @media ".concat(p.sizeQuery, " {\n        padding: 0.75rem 1rem;\n      }\n    ");
  }, function (p) {
    return p.isDisabled ? "\n      background-color: ".concat(primaryDisabledM, ";\n    ") : "\n      cursor: pointer;\n      @media (hover: hover) {\n        &:hover {\n          background-color: ".concat(primaryHighlightM, ";\n        }\n      }\n      &:active {\n        background-color: ").concat(primaryPressedM, ";\n      }\n    ");
  })
};
export default PrimaryButton;